/**
 * 能力表里的 travel.research（联网攻略：心愿的研究会不会真的去查公开资料）。I 在 2026-09-26 加：没开通联网检索时是 not_configured，
 * 说明写着研究一次都不外发、心愿会落到 research_failed、不必给主人“重新研究”按钮（页面本来就没有这个按钮）。
 *
 * 判断照 ./JournalRedraw：读全站共用的 /meta 缓存（queryKeys.meta，和主布局同一个请求、同样的 staleTime，不另外发），只有 available 才算能查。
 * 还没读到、读失败、不是 available（not_configured、disabled、not_implemented，或者表里没有这一项）都按“不能查”说：
 * 页面不许诺会去查、会补上、还在确认（两套说法见 ./model）。
 * 演示按“能查”说（主窗口 2026-09-26 定）：演示是给人看产品本来的样子，和后端开没开检索无关；它也没有“要点按钮”的问题，
 * 不用像重画按钮那样收起。演示不请求能力表。
 */
import { useQuery } from "@tanstack/react-query";
import type { Capability } from "@/shared/contracts";
import { env, type DataMode } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";

/** 能力表里这一项的键（I 定）。 */
export const RESEARCH_CAPABILITY = "travel.research";

/** 纯判断：能力表里 travel.research 是 available 才算能查；没有能力表（还没读到、读失败）算不能查。 */
export function researchAvailableIn(capabilities: readonly Capability[] | null | undefined): boolean {
  return capabilities?.some((capability) => capability.key === RESEARCH_CAPABILITY && capability.status === "available") ?? false;
}

/** 页面用的判断：演示一律按能查说（给人看产品本来的样子）；live 看能力表。 */
export function researchAvailableFor(dataMode: DataMode, capabilities: readonly Capability[] | null | undefined): boolean {
  if (dataMode === "fixture") return true;
  return researchAvailableIn(capabilities);
}

/**
 * 页面、地图那一行用：查询与 ./JournalRedraw 相同（同一个键、同样的 staleTime、只在 live 发；演示不请求能力表）。
 * 服务在查询函数里才取（地图面板那一行的规矩：服务没给也不把面板带崩，只当不能查）。
 * needed 为假时不发请求、只读缓存（地图面板的规矩：用不着的时候不去问 /meta——没有心愿就没有那一行，用不着问）。
 */
export function useResearchAvailable(needed = true): boolean {
  const services = useServices();
  const meta = useQuery({ queryKey: queryKeys.meta, queryFn: () => services.platform.meta(), staleTime: 60_000, enabled: env.dataMode === "live" && needed });
  return researchAvailableFor(env.dataMode, meta.data?.capabilities);
}
